import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Trash2, Image, Plus, Edit2, Upload, X } from 'lucide-react';
import { Button, Input, Checkbox } from '../components/ui';
import { getEntityId } from '../utils/entityId';
import {
  BrandPayload,
  deleteBrand,
  fetchBrands as loadBrands,
  getApiErrorMessage,
  saveBrand,
  uploadAdminImage,
} from '../services/adminCatalog';

interface Brand {
  id: string;
  name: string;
  description?: string;
  logoUrl?: string;
  website?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

type BrandFormState = {
  name: string;
  description: string;
  logoUrl: string;
  website: string;
  isActive: boolean;
};

const emptyForm = (): BrandFormState => ({
  name: '',
  description: '',
  logoUrl: '',
  website: '',
  isActive: true,
});

const Brands: React.FC = () => {
  const { t } = useTranslation();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [formData, setFormData] = useState<BrandFormState>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      setBrands(await loadBrands<Brand>());
    } catch (error: unknown) {
      console.error('Error fetching brands:', error);
      setBrands([]);
      alert(getApiErrorMessage(error, t('messages.errorSaving', { ns: 'brands', defaultValue: 'Failed to load brands' })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  const filteredBrands = brands.filter(
    (brand) =>
      searchTerm === '' ||
      brand.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      brand.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openCreateModal = () => {
    setEditingBrand(null);
    setFormData(emptyForm());
    setShowModal(true);
  };

  const openEditModal = (brand: Brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name,
      description: brand.description || '',
      logoUrl: brand.logoUrl || '',
      website: brand.website || '',
      isActive: brand.isActive,
    });
    setShowModal(true);
  };

  const handleLogoUpload = async (file: File) => {
    setUploadingLogo(true);
    try {
      const fileUrl = await uploadAdminImage(file);
      setFormData((prev) => ({ ...prev, logoUrl: fileUrl }));
    } catch (error: unknown) {
      alert(getApiErrorMessage(error, t('messages.uploadFailed', { ns: 'brands' })));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert(t('messages.nameRequired', { ns: 'brands' }));
      return;
    }

    setSubmitting(true);
    try {
      const payload: BrandPayload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        logoUrl: formData.logoUrl.trim() || undefined,
        website: formData.website.trim() || undefined,
        isActive: formData.isActive,
      };

      const brandId = editingBrand ? getEntityId(editingBrand) : undefined;
      await saveBrand(payload, brandId);
      setShowModal(false);
      setEditingBrand(null);
      setFormData(emptyForm());
      await fetchBrands();
    } catch (error: unknown) {
      console.error('Error saving brand:', error);
      alert(getApiErrorMessage(error, t('messages.errorSaving', { ns: 'brands', defaultValue: 'Failed to save brand' })));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (brand: Brand) => {
    if (window.confirm(t('messages.confirmDelete', { ns: 'brands', name: brand.name }))) {
      try {
        const brandId = getEntityId(brand);
        if (!brandId) return;
        await deleteBrand(brandId);
        await fetchBrands();
      } catch (error: unknown) {
        console.error('Error deleting brand:', error);
        alert(getApiErrorMessage(error, t('messages.errorDeleting', { ns: 'brands', defaultValue: 'Failed to delete brand' })));
      }
    }
  };

  const handleToggleStatus = async (brand: Brand) => {
    try {
      const brandId = getEntityId(brand);
      if (!brandId) return;
      await saveBrand({ name: brand.name, isActive: !brand.isActive }, brandId);
      await fetchBrands();
    } catch (error: unknown) {
      console.error('Error updating brand status:', error);
      alert(getApiErrorMessage(error, t('messages.errorSaving', { ns: 'brands', defaultValue: 'Failed to update brand' })));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const BrandCard: React.FC<{ brand: Brand }> = ({ brand }) => (
    <div className="card p-6 hover:shadow-card-hover transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3 flex-1">
          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
            {brand.logoUrl ? (
              <img
                src={brand.logoUrl}
                alt={brand.name}
                className="w-full h-full object-contain p-1"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            ) : (
              <Image size={24} className="text-gray-400" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-lg truncate mb-2">{brand.name}</h3>
            {brand.description && (
              <p className="text-gray-600 text-sm line-clamp-2 break-words">{brand.description}</p>
            )}
            {brand.website && (
              <a
                href={brand.website.startsWith('http') ? brand.website : `https://${brand.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-custom-primary hover:underline mt-1 inline-block truncate max-w-full"
              >
                {brand.website}
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col space-y-3">
        <div className="flex items-center flex-wrap gap-2">
          <span className={`badge-${brand.isActive ? 'green' : 'red'}`}>
            {brand.isActive ? t('status.active', { ns: 'common' }) : t('status.inactive', { ns: 'common' })}
          </span>
          <span className="text-xs text-gray-500">
            {t('created', { ns: 'brands' })} {formatDate(brand.createdAt)}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              onClick={() => handleToggleStatus(brand)}
              variant={brand.isActive ? 'outline' : 'secondary'}
              size="sm"
            >
              {brand.isActive ? t('buttons.deactivate', { ns: 'common' }) : t('buttons.activate', { ns: 'common' })}
            </Button>
            <button
              onClick={() => openEditModal(brand)}
              className="text-custom-primary hover:text-custom-primary-hover p-1"
              title={t('editBrand', { ns: 'brands' })}
            >
              <Edit2 size={16} />
            </button>
          </div>
          <button
            onClick={() => handleDelete(brand)}
            className="text-red-600 hover:text-red-700 p-1"
            title={t('buttons.delete', { ns: 'common', defaultValue: 'Delete' })}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('title', { ns: 'brands' })}</h1>
          <p className="text-gray-600 mt-2">{t('subtitle', { ns: 'brands' })}</p>
        </div>
        <Button onClick={openCreateModal} icon={Plus} variant="primary">
          {t('addBrand', { ns: 'brands' })}
        </Button>
      </div>

      <div className="card p-6">
        <div className="max-w-md">
          <Input
            type="text"
            placeholder={t('search', { ns: 'brands' })}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
            variant="search"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-primary"></div>
          </div>
        ) : filteredBrands.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            {searchTerm ? t('noSearchResults', { ns: 'brands' }) : t('table.noData', { ns: 'common' })}
          </div>
        ) : (
          filteredBrands.map((brand) => <BrandCard key={brand.id} brand={brand} />)
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingBrand ? t('modal.editTitle', { ns: 'brands' }) : t('modal.createTitle', { ns: 'brands' })}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label={t('fields.name', { ns: 'brands' })}
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('fields.namePlaceholder', { ns: 'brands' })}
                required
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.description', { ns: 'brands' })}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field w-full"
                  rows={3}
                  placeholder={t('fields.descriptionPlaceholder', { ns: 'brands' })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.logo', { ns: 'brands' })}
                </label>
                <div
                  className={`border-2 border-dashed rounded-2xl p-6 text-center transition-colors ${
                    dragActive ? 'border-custom-primary bg-custom-secondary/20' : 'border-gray-300'
                  }`}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) handleLogoUpload(file);
                  }}
                >
                  {formData.logoUrl ? (
                    <div className="relative inline-block">
                      <img
                        src={formData.logoUrl}
                        alt=""
                        className="max-h-32 rounded-lg mx-auto object-contain"
                      />
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        onClick={() => setFormData((prev) => ({ ...prev, logoUrl: '' }))}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                      <p className="text-sm text-gray-600">
                        {uploadingLogo
                          ? t('fields.uploading', { ns: 'brands' })
                          : t('fields.uploadLogo', { ns: 'brands' })}
                      </p>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    id="brand-logo-upload"
                    disabled={uploadingLogo}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleLogoUpload(file);
                      e.target.value = '';
                    }}
                  />
                  <label
                    htmlFor="brand-logo-upload"
                    className="mt-3 inline-block text-sm text-custom-primary cursor-pointer hover:underline"
                  >
                    {uploadingLogo
                      ? t('fields.uploading', { ns: 'brands' })
                      : t('fields.uploadLogo', { ns: 'brands' })}
                  </label>
                </div>
              </div>

              <Input
                label={t('fields.website', { ns: 'brands' })}
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder={t('fields.websitePlaceholder', { ns: 'brands' })}
              />

              <Checkbox
                id="brand-isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                label={t('fields.isActive', { ns: 'brands' })}
                variant="default"
              />

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" onClick={() => setShowModal(false)} variant="outline" disabled={submitting}>
                  {t('buttons.cancel', { ns: 'common' })}
                </Button>
                <Button type="submit" disabled={submitting || !formData.name.trim()} variant="primary" loading={submitting}>
                  {editingBrand ? t('buttons.update', { ns: 'brands' }) : t('buttons.create', { ns: 'brands' })}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Brands;
